/**
 * Keystone RFP Agent - Comprehensive Query Router
 * Maps search queries to relevant RFP entity types with 15+ keywords per entity type
 * Based on Policy Pack v2.0 with 20 entity types and 356 fields
 */

export interface KeystoneEntityTypeMapping {
  entityType: string;
  keywords: string[];
  category: string;
  priority: number; // Higher = more likely to be relevant
}

/**
 * Comprehensive keyword mapping for all 20 Keystone RFP entity types
 * Organized by category as defined in keystone_rfp_agent_v2.yaml
 */
export const KEYSTONE_RFP_ENTITY_MAPPINGS: KeystoneEntityTypeMapping[] = [
  // ========================================
  // Government Opportunities (3 types)
  // ========================================
  {
    entityType: 'govt_opportunity',
    keywords: [
      'opportunity', 'government opportunity', 'govt opp', 'rfp', 'request for proposal', 
      'solicitation', 'tender', 'bid opportunity', 'procurement', 'contract opportunity',
      'sam.gov', 'grants.gov', 'federal opportunity', 'state opportunity', 'municipal',
      'opportunity number', 'naics', 'psc code', 'set aside', 'competitive', 'sole source'
    ],
    category: 'Government Opportunities',
    priority: 10,
  },
  {
    entityType: 'govt_grant',
    keywords: [
      'grant', 'government grant', 'federal grant', 'state grant', 'funding opportunity',
      'grant opportunity', 'cfda', 'assistance listing', 'grant award', 'grant funding',
      'grant program', 'research grant', 'development grant', 'cooperative agreement',
      'financial assistance', 'grant eligibility', 'grant deadline', 'grant application'
    ],
    category: 'Government Opportunities',
    priority: 9,
  },
  {
    entityType: 'govt_contract_award',
    keywords: [
      'contract award', 'government contract', 'federal contract', 'contract win',
      'award notice', 'contract value', 'prime contractor', 'subcontractor', 'awarded',
      'contract history', 'past award', 'contract performance', 'government spending',
      'usaspending', 'fpds', 'contract action', 'obligation', 'award amount'
    ],
    category: 'Government Opportunities',
    priority: 8,
  },

  // ========================================
  // Internal Documents (5 types)
  // ========================================
  {
    entityType: 'proposal_document',
    keywords: [
      'proposal', 'proposal document', 'bid proposal', 'technical proposal', 'cost proposal',
      'proposal volume', 'proposal section', 'proposal draft', 'proposal submission',
      'response document', 'proposal narrative', 'proposal attachment', 'proposal writing',
      'proposal review', 'proposal version', 'proposal template', 'proposal content',
      'technical approach', 'management approach', 'past performance narrative'
    ],
    category: 'Internal Documents',
    priority: 10,
  },
  {
    entityType: 'workplan_document',
    keywords: [
      'workplan', 'work plan', 'project plan', 'implementation plan', 'execution plan',
      'schedule', 'timeline', 'milestone', 'task plan', 'project schedule', 'gantt',
      'work breakdown', 'wbs', 'project timeline', 'delivery schedule', 'project phases',
      'task assignment', 'resource allocation', 'project management plan'
    ],
    category: 'Internal Documents',
    priority: 9,
  },
  {
    entityType: 'rfp_document',
    keywords: [
      'rfp', 'request for proposal', 'solicitation document', 'rfp package', 'rfp section',
      'statement of work', 'sow', 'scope of work', 'requirements', 'evaluation criteria',
      'rfp amendment', 'rfp question', 'rfp clarification', 'solicitation notice',
      'rfp instructions', 'submission requirements', 'performance work statement', 'pws'
    ],
    category: 'Internal Documents',
    priority: 10,
  },
  {
    entityType: 'bid_document',
    keywords: [
      'bid', 'bid document', 'bid submission', 'bid package', 'sealed bid', 'bid response',
      'bid form', 'price proposal', 'cost proposal', 'bid pricing', 'bid schedule',
      'commercial proposal', 'financial proposal', 'pricing schedule', 'cost breakdown',
      'bid evaluation', 'bid comparison', 'competitive bid', 'bid analysis'
    ],
    category: 'Internal Documents',
    priority: 8,
  },
  {
    entityType: 'submission_document',
    keywords: [
      'submission', 'submitted document', 'final submission', 'proposal submission',
      'submitted proposal', 'delivered document', 'submission package', 'uploaded document',
      'electronic submission', 'hard copy submission', 'submission confirmation',
      'submission deadline', 'submission status', 'submission tracking', 'submitted bid'
    ],
    category: 'Internal Documents',
    priority: 7,
  },

  // ========================================
  // Company Intelligence (5 types)
  // ========================================
  {
    entityType: 'staff_member',
    keywords: [
      'staff', 'employee', 'team member', 'personnel', 'staff member', 'consultant',
      'project manager', 'engineer', 'analyst', 'specialist', 'director', 'lead',
      'key personnel', 'resourcing', 'staff allocation', 'team roster', 'org chart',
      'employee profile', 'staff expertise', 'professional', 'subject matter expert', 'sme',
      'bio', 'resume', 'cv', 'staff qualifications', 'credentials', 'certifications'
    ],
    category: 'Company Intelligence',
    priority: 10,
  },
  {
    entityType: 'past_project',
    keywords: [
      'past project', 'previous project', 'project history', 'completed project',
      'project experience', 'portfolio', 'case study', 'project reference', 'client work',
      'similar project', 'relevant experience', 'past performance', 'project portfolio',
      'project delivery', 'project outcome', 'success story', 'project description',
      'project scope', 'project value', 'client reference'
    ],
    category: 'Company Intelligence',
    priority: 9,
  },
  {
    entityType: 'client_record',
    keywords: [
      'client', 'customer', 'client record', 'client profile', 'client information',
      'customer profile', 'government client', 'federal client', 'agency client',
      'client relationship', 'client history', 'client contact', 'client account',
      'client engagement', 'client satisfaction', 'repeat client', 'incumbent'
    ],
    category: 'Company Intelligence',
    priority: 8,
  },
  {
    entityType: 'capability_statement',
    keywords: [
      'capability', 'capability statement', 'capabilities', 'core competency',
      'company capability', 'technical capability', 'service offering', 'expertise',
      'qualification', 'company profile', 'company overview', 'corporate capability',
      'differentiator', 'competitive advantage', 'unique qualification', 'specialized skill',
      'cage code', 'duns', 'naics code', 'socioeconomic status', 'certification'
    ],
    category: 'Company Intelligence',
    priority: 8,
  },
  {
    entityType: 'sop_reference',
    keywords: [
      'sop', 'standard operating procedure', 'procedure', 'process', 'methodology',
      'best practice', 'guideline', 'work instruction', 'quality process', 'iso procedure',
      'cmmi', 'process document', 'operational procedure', 'quality manual', 'policy',
      'standard practice', 'work process', 'quality assurance', 'quality control'
    ],
    category: 'Company Intelligence',
    priority: 7,
  },

  // ========================================
  // Opportunity Analysis (2 types)
  // ========================================
  {
    entityType: 'opportunity_requirement',
    keywords: [
      'requirement', 'requirements', 'specification', 'client requirement', 'technical requirement',
      'functional requirement', 'performance requirement', 'compliance requirement',
      'mandatory requirement', 'evaluation factor', 'evaluation criteria', 'must have',
      'qualification requirement', 'eligibility requirement', 'submission requirement',
      'deliverable', 'acceptance criteria', 'success criteria', 'performance metric'
    ],
    category: 'Opportunity Analysis',
    priority: 9,
  },
  {
    entityType: 'opportunity_match_score',
    keywords: [
      'match score', 'match', 'fit score', 'alignment score', 'compatibility score',
      'win probability', 'score', 'rating', 'ranking', 'opportunity score', 'bid decision',
      'go no-go', 'bid readiness', 'competitive position', 'strength assessment',
      'gap analysis', 'teaming partner', 'partnership', 'strategic fit'
    ],
    category: 'Opportunity Analysis',
    priority: 8,
  },

  // ========================================
  // Evidence & Compliance (3 types)
  // ========================================
  {
    entityType: 'technical_approach',
    keywords: [
      'technical approach', 'technical solution', 'methodology', 'technical methodology',
      'approach', 'solution design', 'technical design', 'architecture', 'implementation approach',
      'technical plan', 'solution approach', 'system design', 'technical strategy',
      'innovation', 'technical innovation', 'solution description', 'technical concept',
      'proof of concept', 'pilot', 'prototype'
    ],
    category: 'Evidence & Compliance',
    priority: 9,
  },
  {
    entityType: 'past_performance_example',
    keywords: [
      'past performance', 'ppq', 'performance example', 'reference', 'project reference',
      'similar work', 'relevant experience', 'performance record', 'past contract',
      'performance history', 'completed work', 'demonstrated experience', 'proven track record',
      'successful delivery', 'contract performance', 'cpars', 'performance rating',
      'client testimonial', 'reference letter', 'letter of recommendation'
    ],
    category: 'Evidence & Compliance',
    priority: 10,
  },
  {
    entityType: 'qualification_certification',
    keywords: [
      'qualification', 'certification', 'certificate', 'accreditation', 'credential',
      'professional certification', 'industry certification', 'license', 'registration',
      'iso certification', 'cmmi', 'security clearance', 'facility clearance',
      'professional license', 'technical certification', 'training certificate',
      'quality certification', '8a', 'hubzone', 'wosb', 'sdvosb', 'vosb', 'small business'
    ],
    category: 'Evidence & Compliance',
    priority: 9,
  },

  // ========================================
  // Project Execution (2 types)
  // ========================================
  {
    entityType: 'project_team_composition',
    keywords: [
      'team', 'project team', 'team composition', 'team structure', 'team member',
      'org chart', 'organization chart', 'staffing plan', 'resourcing plan', 'team roster',
      'key personnel', 'labor category', 'labor mix', 'team allocation', 'role assignment',
      'project organization', 'management team', 'technical team', 'support staff',
      'labor hours', 'staffing level', 'full time equivalent', 'fte'
    ],
    category: 'Project Execution',
    priority: 8,
  },
  {
    entityType: 'budget_estimate',
    keywords: [
      'budget', 'cost', 'pricing', 'estimate', 'budget estimate', 'cost estimate',
      'price estimate', 'financial estimate', 'cost breakdown', 'budget breakdown',
      'labor cost', 'material cost', 'overhead', 'indirect cost', 'direct cost',
      'total cost', 'project cost', 'contract value', 'price', 'fee', 'profit',
      'cost proposal', 'rom', 'rough order of magnitude', 'basis of estimate', 'boe'
    ],
    category: 'Project Execution',
    priority: 8,
  },
];

/**
 * Intelligent query routing for Keystone RFP entities
 * Returns relevant entity types based on query keywords with scoring
 */
export function routeKeystoneQuery(query: string, enabledTypes: string[]): string[] {
  const queryLower = query.toLowerCase();
  const matches: Array<{ entityType: string; score: number }> = [];

  KEYSTONE_RFP_ENTITY_MAPPINGS.forEach((mapping) => {
    if (!enabledTypes.includes(mapping.entityType)) return;

    let score = 0;
    mapping.keywords.forEach((keyword) => {
      if (queryLower.includes(keyword.toLowerCase())) {
        score += mapping.priority;
      }
    });

    if (score > 0) {
      matches.push({ entityType: mapping.entityType, score });
    }
  });

  // Sort by score and return top matches
  matches.sort((a, b) => b.score - a.score);

  // If we have matches, return top 10
  if (matches.length > 0) {
    return matches.slice(0, 10).map((m) => m.entityType);
  }

  // Default fallback - commonly useful RFP types
  return [
    'govt_opportunity',
    'proposal_document',
    'rfp_document',
    'staff_member',
    'past_performance_example',
  ];
}

/**
 * Get all entity types for a specific category
 */
export function getEntityTypesByCategory(category: string): string[] {
  return KEYSTONE_RFP_ENTITY_MAPPINGS
    .filter((m) => m.category === category)
    .map((m) => m.entityType);
}

/**
 * Get all categories
 */
export function getAllCategories(): string[] {
  const categories = new Set(KEYSTONE_RFP_ENTITY_MAPPINGS.map((m) => m.category));
  return Array.from(categories);
}

/**
 * Get entity type details
 */
export function getEntityTypeMapping(entityType: string): KeystoneEntityTypeMapping | undefined {
  return KEYSTONE_RFP_ENTITY_MAPPINGS.find((m) => m.entityType === entityType);
}


